// src/features/tasks/hooks/useTaskEquipment.js
import { useState, useCallback } from 'react';
import { taskEquipmentAPI } from '../services/taskEquipment';
import { useApi } from '../../../hooks/api/useApi';

export function useTaskEquipment() {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { execute } = useApi();

  /**
   * List equipment linked to a task
   * @param {string} taskId - The task's ID
   * @returns {Promise<Object>} List of equipment relationships
   */
  const listTaskEquipment = useCallback(async (taskId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await execute(() => taskEquipmentAPI.listTaskEquipment(taskId));

      if (result?.success) {
        setEquipment(result.data.data.relationships || []);
      }

      setLoading(false);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch task equipment';
      setError(errorMessage);
      setLoading(false);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [execute]);

  /**
   * Link equipment to a task
   * @param {string} taskId - The task's ID
   * @param {Object} data - Equipment link data
   * @param {string} data.equipment_id - UUID of equipment to link
   * @param {string} data.relation_type - Type of relationship (KONSTANS, ARTIFAKT, etc.)
   * @param {string} data.notes - Optional notes about the relationship
   * @returns {Promise<Object>} Link result
   */
  const linkEquipmentToTask = useCallback(async (taskId, data) => {
    setLoading(true);
    setError(null);

    try {
      const result = await execute(() => 
        taskEquipmentAPI.linkEquipmentToTask(taskId, data)
      );

      if (result?.success) {
        // Refresh equipment list
        await listTaskEquipment(taskId);
      }

      setLoading(false);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to link equipment to task';
      setError(errorMessage);
      setLoading(false);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [execute, listTaskEquipment]);

  /**
   * Update equipment relationship metadata
   * @param {string} taskId - The task's ID
   * @param {string} taskEquipmentId - The relationship's ID
   * @param {Object} data - Update data
   * @returns {Promise<Object>} Update result
   */
  const updateTaskEquipment = useCallback(async (taskId, taskEquipmentId, data) => {
    setLoading(true);
    setError(null);

    try {
      const result = await execute(() => 
        taskEquipmentAPI.updateTaskEquipment(taskId, taskEquipmentId, data)
      );

      if (result?.success) {
        // Refresh equipment list
        await listTaskEquipment(taskId);
      }

      setLoading(false);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update task equipment';
      setError(errorMessage);
      setLoading(false);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [execute, listTaskEquipment]);

  /**
   * Check or uncheck an equipment link for the current task occurrence
   * @param {string} taskId - The task's ID
   * @param {string} taskEquipmentId - The relationship's ID
   * @param {boolean} isChecked - Whether the link should be checked
   * @returns {Promise<Object>} Update result
   */
  const setEquipmentChecked = useCallback(async (taskId, taskEquipmentId, isChecked) => {
    setError(null);

    try {
      const result = await execute(() =>
        taskEquipmentAPI.setTaskEquipmentChecked(taskId, taskEquipmentId, isChecked)
      );

      if (result?.success) {
        const updated = result.data.data;
        setEquipment((prev) =>
          prev.map((item) =>
            item.task_equipment_id === taskEquipmentId
              ? { ...item, ...updated }
              : item
          )
        );
      }

      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update equipment checklist state';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [execute]);

  /**
   * Unlink equipment from a task
   * @param {string} taskId - The task's ID
   * @param {string} taskEquipmentId - The relationship's ID to remove
   * @returns {Promise<Object>} Unlink result
   */
  const unlinkEquipmentFromTask = useCallback(async (taskId, taskEquipmentId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await execute(() => 
        taskEquipmentAPI.unlinkEquipmentFromTask(taskId, taskEquipmentId)
      );

      if (result?.success) {
        // Refresh equipment list
        await listTaskEquipment(taskId);
      }

      setLoading(false);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to unlink equipment from task';
      setError(errorMessage);
      setLoading(false);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [execute, listTaskEquipment]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear equipment state
   */
  const clearEquipment = useCallback(() => {
    setEquipment([]);
  }, []);

  return {
    loading,
    error,
    equipment,
    // Actions
    listTaskEquipment,
    linkEquipmentToTask,
    updateTaskEquipment,
    setEquipmentChecked,
    unlinkEquipmentFromTask,
    clearError,
    clearEquipment,
  };
}